"use client";

import { AnimatePresence, motion } from "motion/react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { Slot } from "radix-ui";
import { Fragment, type ReactNode, isValidElement, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { cn, useMediaQuery } from "../..";
import { Dialog } from "./dialog/Default";
import { Button } from "./interactive/Button";

interface TooltipProps extends Omit<TooltipPrimitive.TooltipProps, "open" | "onOpenChange"> {
	content: ReactNode;
	title?: string;
	side?: TooltipPrimitive.TooltipContentProps["side"];
	sideOffset?: TooltipPrimitive.TooltipContentProps["sideOffset"];
	color?: "shades-light" | "dark" | "primary-dark";
	allowTriggerClick?: boolean;
	hideOnTouchDevice?: boolean;
	disabled?: boolean;
	className?: string;
}

export function Tooltip(props: TooltipProps) {
	const {
		side = "bottom",
		sideOffset = 1,
		content,
		color = "dark",
		allowTriggerClick = false,
		hideOnTouchDevice = false,
		children,
		className,
		...remainingProps
	} = props;

	const intl = useIntl();
	const isPointerDevice = useMediaQuery({ pointer: "no-touch" });
	const [open, onOpenChange] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const renderButton = useMemo(() => {
		if (isValidElement(children)) {
			return (
				typeof children.props === "object" &&
				children.props !== null &&
				("defaultMessage" in children.props ||
					Object.values(children.props).includes("formatMessage"))
			);
		}

		return typeof children === "string";
	}, [children]);

	const title =
		props.title ?? intl.formatMessage({ defaultMessage: "Meer informatie", id: "Mv6ZTa" });

	if (props.disabled || (!isPointerDevice && hideOnTouchDevice)) {
		return (
			<Slot.Root aria-label={title} data-state="closed">
				{children}
			</Slot.Root>
		);
	}

	if (!isPointerDevice) {
		return (
			<Fragment>
				<Slot.Root
					ref={triggerRef}
					onClick={() => onOpenChange((prev) => !prev)}
					aria-label={title}
					data-state="closed"
				>
					{renderButton ? <Button variant="link">{children}</Button> : children}
				</Slot.Root>

				<Dialog open={open} onOpenChange={onOpenChange} title={title}>
					{content}
				</Dialog>
			</Fragment>
		);
	}

	return (
		<TooltipPrimitive.Provider delayDuration={props.delayDuration ?? 0}>
			<TooltipPrimitive.Root open={open} onOpenChange={onOpenChange} {...remainingProps}>
				<TooltipPrimitive.Trigger
					asChild
					ref={triggerRef}
					onClick={(event) => {
						if (!allowTriggerClick) {
							return event.preventDefault();
						}
					}}
					aria-label={title}
				>
					{renderButton ? <Button variant="link">{children}</Button> : children}
				</TooltipPrimitive.Trigger>
				<AnimatePresence>
					{open && (
						<TooltipPrimitive.Portal>
							<TooltipPrimitive.Content
								side={side}
								sideOffset={sideOffset}
								className="focus:outline-none"
								onPointerDownOutside={(event) => {
									if (event.target instanceof Element && triggerRef.current) {
										if (triggerRef.current.contains(event.target)) {
											event.preventDefault();
										}
									}
								}}
							>
								<motion.div
									className={cn(
										"max-w-[min(calc(100vw-2rem),20rem)] hyphens-auto break-words rounded px-2 py-1 text-sm shadow",
										{
											"border-shades-light bg-shades-light text-dark": color === "shades-light",
											"bg-dark text-white": color === "dark",
											"bg-primary-dark text-white": color === "primary-dark",
										},
										className,
									)}
									transition={{ duration: 0.15 }}
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									exit={{ opacity: 0, scale: 0.9 }}
								>
									<TooltipPrimitive.Arrow
										className={cn("fill-current", {
											"text-shades-light": color === "shades-light",
											"text-dark": color === "dark",
											"text-primary-dark": color === "primary-dark",
										})}
									/>
									{content}
								</motion.div>
							</TooltipPrimitive.Content>
						</TooltipPrimitive.Portal>
					)}
				</AnimatePresence>
			</TooltipPrimitive.Root>
		</TooltipPrimitive.Provider>
	);
}
